import type {
  FutureCard,
  FutureMarketOption,
  FutureTab,
} from "./futuresDummyData";
import type { SportsbookListTranslations } from "../Translations/listTranslationData";

// Shape of items from GET_FUTURES_LEAGUES (`data.data` or `data.data.items`).
export interface RawFuturesLeagueItem {
  entity_id?: string;
  item_id?: string;
  league_id?: string;
  name?: string;
  display_name?: string;
  country_name?: string;
  display_order?: number | string | null;
}

export interface League {
  id: string;
  name: string;
  order: number;
  country_name?: string;
}

function leagueIdFromRaw(
  item: Pick<RawFuturesLeagueItem, "league_id" | "item_id" | "entity_id">,
  idx: number
): string {
  if (item.league_id) return item.league_id;
  if (item.item_id) return item.item_id;
  if (item.entity_id) {
    return item.entity_id.startsWith("LEAGUE#")
      ? item.entity_id.slice("LEAGUE#".length)
      : item.entity_id;
  }
  return `league_${idx}`;
}

// Futures leagues list: prefer sportradar league_id and sort by display_order.
export function mapRawFuturesLeagues(items: RawFuturesLeagueItem[]): League[] {
  return items
    .map((item, idx) => ({
      id: leagueIdFromRaw(item, idx),
      name: item.display_name || item.name || "",
      order: Number(item.display_order ?? idx),
      country_name: item.country_name,
    }))
    .sort((a, b) => a.order - b.order);
}

// Shape of `data.data` items from GET_FUTURES_MARKETS_AUTOCOMPLETE.
export interface RawFutureMarketAutocompleteItem {
  market_id: string;
  display_name?: string;
  specifier?: string;
}

// The autocomplete endpoint only returns markets, so every option maps to the
// MARKET type. The composite entity id mirrors the futures markets list
// (`<market_id>_<specifier>`) so a selected option matches an existing card.
export function mapRawFutureMarketOptions(
  items: RawFutureMarketAutocompleteItem[]
): FutureMarketOption[] {
  return items.map((item) => ({
    entity_id: item.specifier
      ? `${item.market_id}_${item.specifier}`
      : String(item.market_id),
    name: item.display_name || "",
    type: "MARKET",
    market_id: Number(item.market_id) || 0,
    specifier: item.specifier,
  }));
}

// Shape of `data.data.items` from GET_FUTURES_ENTITIES.
export interface RawFutureMarket {
  market_id: string;
  specifier?: string;
  display_name?: string;
  is_available?: boolean;
}

export interface RawFutureTabItem {
  entity_id?: string;
  item_id?: string;
  display_name?: string;
  display_order?: number | string | null;
  is_active?: boolean;
  market_ids?: string[];
  markets?: RawFutureMarket[];
  translations?: SportsbookListTranslations | null;
}

export interface FuturesListData {
  tabs: FutureTab[];
  cardsByTabId: Record<string, FutureCard[]>;
}

function compositeMarketId(
  market: RawFutureMarket,
  marketIds: string[],
  marketIdx: number
): string {
  if (marketIds[marketIdx]) return marketIds[marketIdx];
  if (market.specifier) return `${market.market_id}_${market.specifier}`;
  return String(market.market_id);
}

export function mapRawFuturesItems(items: RawFutureTabItem[]): FuturesListData {
  const sorted = [...items].sort(
    (a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0)
  );

  const tabs: FutureTab[] = sorted.map((item, idx) => ({
    id: item.item_id || item.entity_id || `tab_${idx}`,
    ename: item.display_name || "",
    order: String(item.display_order ?? idx + 1),
    entity_id: item.entity_id,
    // Default to active when the backend omits the flag.
    is_active: item.is_active ?? true,
    translations: item.translations || null,
  }));

  const cardsByTabId: Record<string, FutureCard[]> = {};
  sorted.forEach((item, idx) => {
    const tabId = item.item_id || item.entity_id || `tab_${idx}`;
    const marketIds = item.market_ids ?? [];
    const markets = item.markets ?? [];

    // Keep the original index so composite ids stay aligned with `market_ids`
    // even after unavailable markets are dropped.
    cardsByTabId[tabId] = markets
      .map((market, marketIdx): FutureCard | null => {
        if (market.is_available === false) return null;
        const entityId = compositeMarketId(market, marketIds, marketIdx);
        const name = market.display_name || entityId;
        return {
          entity_id: entityId,
          market_id: Number(market.market_id) || 0,
          display_name: name,
          original_name: name,
          specifier: market.specifier,
        };
      })
      .filter((card): card is FutureCard => card !== null);
  });

  return { tabs, cardsByTabId };
}

export function cloneFuturesListData(data: FuturesListData): FuturesListData {
  return {
    tabs: data.tabs.map((tab) => ({ ...tab })),
    cardsByTabId: Object.fromEntries(
      Object.entries(data.cardsByTabId).map(([tabId, cards]) => [
        tabId,
        cards.map((card) => ({ ...card })),
      ])
    ),
  };
}
